import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { IconRegistryService } from '@rd/core/services/icon-registry/icon-registry.service';
import { IconOptions } from '@rd/types';

type ComponentRenderState = 'loading' | 'error' | 'content';

@Component({
  selector: 'rd-shared-pagination',
  templateUrl: './pagination.component.html',
  styleUrls: ['./pagination.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PaginationComponent implements OnChanges, OnInit {
  @HostBinding('class.rd-shared-pagination') componentClass = true;
  @Input() currentPage: number;
  @Input() totalPageCount: number;
  @Input() loading: boolean;
  @Input() showDisabledNavigationButtons = false;

  @Output() changePageNumber: EventEmitter<number> = new EventEmitter();
  numberOfPageArray: number[];
  isPaginationEnds: boolean;
  maximumPageCountVisible = 5;

  flippableIconOptions: IconOptions = {
    flippable: true,
  };

  constructor(private iconRegistryService: IconRegistryService) {}

  ngOnInit(): void {
    this.iconRegistryService.registerIcon('pagination-right');
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.currentPage !== undefined) {
      this.paginationInitialize();
    }
    if (changes.totalPageCount !== undefined) {
      this.paginationInitialize();
    }
  }

  private hasValidData(): boolean {
    return !!this.totalPageCount && !!this.currentPage;
  }

  isSelectedPage(page: number): boolean {
    return page === this.currentPage;
  }

  get atFirstPage(): boolean {
    return this.currentPage === 1;
  }

  get atLastPage(): boolean {
    return this.currentPage === this.totalPageCount;
  }

  private setNumberOfPageArray(): void {
    this.numberOfPageArray = [];
    if (this.totalPageCount < 9) {
      this.numberOfPageArray = this.range(this.totalPageCount - 2, 2);
      return;
    }
    if (this.totalPageCount > 5) {
      if (this.currentPage < 4) {
        this.numberOfPageArray = this.range(4, 2);
      } else if (this.totalPageCount - this.currentPage < 3) {
        this.numberOfPageArray = this.range(5, this.totalPageCount - 5);
      } else {
        this.numberOfPageArray = this.range(5, this.currentPage - 2);
      }
    } else {
      this.numberOfPageArray = this.range(this.totalPageCount - 1, 2);
    }
  }

  private range(size: number, startAt: number = 0): Array<number> {
    const rangeArray = new Array();
    for (let index = 0; size > index; index++) {
      const page = startAt + index;
      if (page !== this.totalPageCount) {
        rangeArray.push(startAt + index);
      }
    }
    return rangeArray;
  }

  private paginationInitialize(): void {
    this.checkPaginationEnds();
    this.setNumberOfPageArray();
  }

  checkPaginationEnds(): void {
    this.isPaginationEnds = this.totalPageCount - this.currentPage < 5;
  }

  changePage(pageNumber: number): void {
    if (
      pageNumber < 1 ||
      pageNumber > this.totalPageCount ||
      pageNumber === this.currentPage
    ) {
      return;
    }
    this.changePageNumber.emit(pageNumber);
  }

  shouldShowRightEllipsis(): boolean {
    return (
      this.totalPageCount - this.currentPage > 3 && this.totalPageCount > 8
    );
  }

  shouldShowLeftEllipsis(): boolean {
    return this.currentPage > 4 && this.totalPageCount > 8;
  }
}
